import { useMemo } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  EditableCollectionBinding,
  useCollectionBinding,
} from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { useCtaColorVars } from '../hooks/useCtaColorVars';

// ---------------------------------------------------------------------------
// FAQ — an accordion of question/answer pairs sourced from the `faqs`
// property collection.
//
// Each item has `question` (text), `answer` (richtext), and optional
// `category` (text). When one or more items declare a category, we group
// the accordion under its category headings — matching the editor model
// where ops teams organize FAQs by topic (Leasing, Pets, Maintenance…).
// If no categories are present the whole list renders as a single
// accordion.
//
// Uses native <details>/<summary> so expand/collapse works with zero JS
// (SSG-friendly + accessible).
// ---------------------------------------------------------------------------

interface FAQItem {
  question?: string | null;
  answer?: string | null;
  category?: string | null;
  [key: string]: unknown;
}

const UNCATEGORIZED = '__uncategorized__';

/** Group FAQ items by their (trimmed, lowercased) category key, preserving
 *  the first-seen label so editor casing wins ("Leasing" not "leasing"). */
function groupByCategory(items: FAQItem[]): Array<{ key: string; label: string; items: FAQItem[] }> {
  const groups = new Map<string, { label: string; items: FAQItem[] }>();
  for (const item of items) {
    if (!hasValue(item.question)) continue;
    const raw = (item.category || '').trim();
    const key = raw ? raw.toLowerCase() : UNCATEGORIZED;
    const label = raw || 'General';
    const existing = groups.get(key);
    if (existing) {
      existing.items.push(item);
    } else {
      groups.set(key, { label, items: [item] });
    }
  }
  return Array.from(groups.entries()).map(([key, { label, items }]) => ({
    key,
    label,
    items,
  }));
}

function hasValue(val: unknown): boolean {
  if (val === null || val === undefined) return false;
  // Treat richtext that is only empty tags / whitespace as "no value".
  const text = String(val)
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, '')
    .trim();
  return text !== '';
}

/** The runtime passes richtext fields through as HTML strings for text, and
 *  plain text questions as plain strings. Wrap plain-text questions in a
 *  <p> so the HTML rendering path works uniformly for both. */
function toHtml(val: unknown): string {
  const s = String(val ?? '').trim();
  if (!s) return '';
  // Already HTML? (looks like a tag at the start)
  if (/^</.test(s)) return s;
  // Plain text — escape and wrap.
  const escaped = s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  return `<p>${escaped}</p>`;
}

export function FAQ() {
  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();
  const ctaColorVars = useCtaColorVars();

  const items = useCollectionBinding<FAQItem>('faq.items', {
    collection: 'faqs',
  });

  const groups = useMemo(() => groupByCategory(items || []), [items]);
  const multipleCategories = groups.length > 1;
  const hasAny = groups.some((g) => g.items.length > 0);

  return (
    <section
      ref={sectionRef}
      style={ctaColorVars}
      className="reveal-fade-up pt-6 pb-16 md:pt-8 md:pb-24 bg-[var(--vibe-color-background)]"
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <EditableCollectionBinding bindingPath="faq.items" label="FAQs">
          {hasAny ? (
            <div className="faq-groups">
              {groups.map((group) => (
                <div key={group.key} className="faq-group">
                  {multipleCategories && (
                    <h2 className="faq-category-heading faq-heading-font">
                      {group.label}
                    </h2>
                  )}
                  <div className="faq-list">
                    {group.items.map((item, i) => (
                      <details key={`${group.key}-${i}`} className="faq-item">
                        <summary className="faq-summary">
                          <span
                            className="faq-question faq-heading-font"
                            dangerouslySetInnerHTML={{ __html: toHtml(item.question) }}
                          />
                          <span aria-hidden="true" className="faq-icon" />
                        </summary>
                        <div
                          className="faq-answer faq-body-font"
                          dangerouslySetInnerHTML={{ __html: toHtml(item.answer) }}
                        />
                      </details>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="faq-empty faq-body-font text-center text-[var(--vibe-color-text-muted)]">
              <p>Frequently asked questions will appear here once added.</p>
            </div>
          )}
        </EditableCollectionBinding>
      </div>

      <style>{`
        /* Category headings + questions: Nunito Sans at normal weight (loaded
           by the Header via the css2 link), applied to all rich-text children. */
        .faq-heading-font,
        .faq-heading-font * {
          font-family: 'Nunito Sans', sans-serif;
          font-weight: 400;
        }
        /* Answers: Work Sans at normal weight/style (loaded by the Header). */
        .faq-body-font,
        .faq-body-font * {
          font-family: 'Work Sans', sans-serif;
          font-weight: 400;
          font-style: normal;
        }

        .faq-groups {
          display: flex;
          flex-direction: column;
          gap: 2.5rem;
        }

        .faq-category-heading {
          font-size: clamp(1.5rem, 2.2vw, 2rem);
          color: var(--cta-bg, var(--vibe-color-secondary));
          text-transform: uppercase;
          letter-spacing: 0.04em;
          margin: 0 0 1rem;
          padding-bottom: 0.5rem;
          border-bottom: 2px solid var(--vibe-color-primary);
          text-align: left;
        }
        /* Center the category title on mobile — the long underline reads
           as a divider across the narrow column, so a centered heading
           sits more naturally above it than a left-anchored one. */
        @media (max-width: 639px) {
          .faq-category-heading {
            text-align: center;
          }
        }

        .faq-list {
          display: flex;
          flex-direction: column;
          border-top: 1px solid #e8e8ea;
        }

        .faq-item {
          border-bottom: 1px solid #e8e8ea;
        }

        .faq-summary {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1.5rem;
          padding: 1.35rem 0.25rem;
          cursor: pointer;
          list-style: none;
          user-select: none;
        }
        /* Hide the default disclosure triangle across browsers. */
        .faq-summary::-webkit-details-marker {
          display: none;
        }

        .faq-question {
          font-size: 1.05rem;
          line-height: 1.4;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          color: var(--vibe-color-text);
        }
        .faq-question p {
          margin: 0;
        }

        /* +/- indicator built from two bars; the vertical bar collapses when
           the item is open, leaving a minus sign. */
        .faq-icon {
          position: relative;
          flex: 0 0 auto;
          width: 16px;
          height: 16px;
        }
        .faq-icon::before,
        .faq-icon::after {
          content: '';
          position: absolute;
          background: var(--vibe-color-primary);
          transition: transform 0.25s ease, opacity 0.25s ease;
        }
        .faq-icon::before {
          top: 50%;
          left: 0;
          width: 100%;
          height: 2px;
          transform: translateY(-50%);
        }
        .faq-icon::after {
          left: 50%;
          top: 0;
          width: 2px;
          height: 100%;
          transform: translateX(-50%);
        }
        .faq-item[open] .faq-icon::after {
          transform: translateX(-50%) scaleY(0);
          opacity: 0;
        }

        .faq-answer {
          padding: 0 0.25rem 1.5rem;
          font-size: 0.95rem;
          line-height: 1.7;
          color: var(--vibe-color-text-muted);
        }
        .faq-answer p {
          margin: 0 0 0.75rem;
        }
        .faq-answer p:last-child {
          margin-bottom: 0;
        }
        .faq-answer a {
          color: var(--vibe-color-primary);
          text-decoration: underline;
        }
        .faq-answer ul {
          list-style: disc;
          padding-left: 1.25rem;
          margin: 0 0 0.75rem;
        }
        .faq-answer ol {
          list-style: decimal;
          padding-left: 1.25rem;
          margin: 0 0 0.75rem;
        }
        .faq-answer li {
          margin-bottom: 0.35rem;
        }

        .faq-empty {
          padding: 2.5rem 0;
        }
      `}</style>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'FAQ',
  displayName: 'FAQ Accordion',
  tokens: {},
  collectionBindings: {
    'faq.items': {
      label: 'FAQs',
      collection: 'faqs',
    },
  },
};

(FAQ as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default FAQ;
